// API URL - uses relative path that Cloudflare Worker proxies to Azure
// In development: Vite proxy handles /api → http://localhost:7071/api
// In production: Cloudflare Worker handles /api → https://mymeds-api.azurewebsites.net/api
const apiURLroot = "/api/";

// Local storage key for cached failed uploads
const FAILED_UPLOADS_KEY = "relmed_failed_uploads";
const CACHED_UPLOAD_SUCCEEDED_EVENT = "mymeds:cached-upload-succeeded";
const CACHED_UPLOAD_FAILED_EVENT = "mymeds:cached-upload-failed";

// Cached uploads are dropped once they are this old, so nothing retries
// indefinitely. Sessions can be a fortnight apart, so the window has to be
// generous enough to survive a participant not logging in for a while.
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Replay discipline (ported from RELMED 2026-09-16). On 2026-09-15 a RELMED
// participant's laptop replayed ~180 stale autosaves — all at once, on every
// token refresh — producing hundreds of concurrent writes to one Cosmos
// document, clobbering current state and overwriting a good REDCap file
// with a stale snapshot. Hence: one replay at a time, oldest first,
// newest-per-target only, and items past MAX_REPLAY_ATTEMPTS are parked
// (kept under PARKED_UPLOADS_KEY for support, never sent automatically).
const PARKED_UPLOADS_KEY = "relmed_parked_uploads";
const MAX_REPLAY_ATTEMPTS = 5;
const REPLAY_GAP_MS = 250;
let isProcessingUploads = false;

// Leaf paths of a partial-update payload, e.g.
//   { assignments: { 3: { tasklist: { 0: { state: { state, timestamp } } } } } }
//   → ["assignments/3/tasklist/0/state/state", ".../state/timestamp"]
// Two PATCHes touching the same paths supersede each other; only the newest
// is worth sending. `id` is the target, not a value, so it is ignored.
function leafPaths(value, prefix = "") {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return [prefix];
  }
  const keys = Object.keys(value);
  if (keys.length === 0) return [prefix];
  return keys.flatMap((k) => leafPaths(value[k], prefix ? `${prefix}/${k}` : k));
}

function coalesceKey(upload) {
  if ((upload.method || "").toUpperCase() === "POST") {
    // post-data replaces the record's file wholesale: newest snapshot wins
    const target = upload.data?.record_id ?? upload.id;
    return `POST ${upload.endpoint} ${target}`;
  }
  const paths = leafPaths(upload.data || {})
    .filter((p) => p !== "id")
    .sort()
    .join(",");
  return `PATCH ${upload.endpoint} ${paths}`;
}

function writeUploads(key, items) {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch (error) {
    console.error(`Failed to write ${key}:`, error);
  }
}

function parkUploads(uploads, reason) {
  if (uploads.length === 0) return;
  const parkedIds = new Set(uploads.map((u) => u.id));
  let parked = [];
  try {
    parked = JSON.parse(localStorage.getItem(PARKED_UPLOADS_KEY) || "[]");
  } catch (e) {
    parked = [];
  }
  writeUploads(PARKED_UPLOADS_KEY, [
    ...parked,
    ...uploads.map((u) => ({ ...u, parkedAt: Date.now(), reason })),
  ]);
  writeUploads(
    FAILED_UPLOADS_KEY,
    readCachedUploads().filter((u) => !parkedIds.has(u.id))
  );
  console.warn(`Parked ${uploads.length} cached upload(s): ${reason}`);
}

function recordAttempt(uploadId) {
  const updated = readCachedUploads().map((u) =>
    u.id === uploadId
      ? { ...u, attempts: (u.attempts || 0) + 1, lastAttemptAt: Date.now() }
      : u
  );
  writeUploads(FAILED_UPLOADS_KEY, updated);
  const item = updated.find((u) => u.id === uploadId);
  if (item && item.attempts >= MAX_REPLAY_ATTEMPTS) {
    parkUploads([item], `${MAX_REPLAY_ATTEMPTS} failed replay attempts`);
  }
}

function isRetryableStatus(status) {
  // 401 is terminal for the token that was used but not for the upload:
  // processFailedUploads runs immediately after a new token is acquired
  // (MsalContext), so re-authenticating is exactly what fixes it. The only
  // caller relying on this cache is postTaskData, so treating an expired
  // token as permanent would silently discard a participant's completed
  // task data. CACHE_MAX_AGE_MS is what stops it retrying forever.
  //
  // 403 stays terminal — that is an authorisation decision about an
  // identity we already authenticated, so logging in again won't change it.
  if (status === 401) return true;
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

// Read the cache, dropping anything past CACHE_MAX_AGE_MS. Records written
// before timestamps were relied upon have no usable age, so they are kept
// and allowed to age out from first read.
function readCachedUploads() {
  let uploads;
  try {
    uploads = JSON.parse(localStorage.getItem(FAILED_UPLOADS_KEY) || "[]");
  } catch (error) {
    console.error("Failed to read cached uploads:", error);
    return [];
  }
  if (!Array.isArray(uploads)) return [];

  const now = Date.now();
  const live = uploads.filter((upload) => {
    const timestamp = Number(upload?.timestamp);
    if (!Number.isFinite(timestamp)) return true;
    return now - timestamp < CACHE_MAX_AGE_MS;
  });

  if (live.length !== uploads.length) {
    console.log(`Dropped ${uploads.length - live.length} expired cached upload(s)`);
    try {
      localStorage.setItem(FAILED_UPLOADS_KEY, JSON.stringify(live));
    } catch (error) {
      console.error("Failed to prune cached uploads:", error);
    }
  }
  return live;
}

function isUploadOwnedByUser(upload, currentUserId) {
  if (!currentUserId) return false;

  const ownerIds = [upload?.participantId, upload?.data?.id].filter(Boolean);
  return ownerIds.some(
    (ownerId) =>
      ownerId === currentUserId ||
      (typeof ownerId === "string" &&
        ownerId.startsWith(`${currentUserId}_`))
  );
}

// Save failed upload to localStorage
function cacheFailedUpload(endpoint, data, participantId, method) {
  // Changed accessToken to participantId
  try {
    // Get existing cached uploads, expiring any that are past the age cap
    const existingUploads = readCachedUploads();

    // Add new failed upload with timestamp
    existingUploads.push({
      method: method.toUpperCase(),
      endpoint,
      data,
      participantId, // Store participantId
      timestamp: Date.now(),
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    });

    // Save back to localStorage
    localStorage.setItem(FAILED_UPLOADS_KEY, JSON.stringify(existingUploads));

    console.log(`Upload cached for later retry: ${endpoint}`);
    return true;
  } catch (error) {
    console.error("Failed to cache upload:", error);
    return false;
  }
}

// Remove a successful upload from cache
function removeFromCache(uploadId) {
  try {
    const existingUploads = JSON.parse(
      localStorage.getItem(FAILED_UPLOADS_KEY) || "[]"
    );
    const filteredUploads = existingUploads.filter(
      (upload) => upload.id !== uploadId
    );
    localStorage.setItem(FAILED_UPLOADS_KEY, JSON.stringify(filteredUploads));
  } catch (error) {
    console.error("Failed to remove upload from cache:", error);
  }
}

// Replay cached uploads: single-flight, oldest first, one request at a time,
// newest-per-target only. Returns a summary for callers that want to show it.
async function processFailedUploads(accessToken) {
  if (isProcessingUploads) {
    return { skipped: "already running" };
  }
  if (!accessToken) {
    console.log("No access token provided for retry processing");
    return { skipped: "no token" };
  }

  // Decode token to get current user ID
  let currentUserId;
  try {
    const tokenPayload = JSON.parse(atob(accessToken.split(".")[1]));
    currentUserId = tokenPayload.oid || tokenPayload.sub;
  } catch (error) {
    console.error("Failed to decode access token for user validation:", error);
    return { skipped: "bad token" };
  }
  if (!currentUserId) {
    console.error("Could not extract user ID from access token");
    return { skipped: "no user id" };
  }

  isProcessingUploads = true;
  const summary = { parked: 0, superseded: 0, sent: 0, failed: 0, skipped: 0 };
  try {
    // readCachedUploads already drops anything past CACHE_MAX_AGE_MS.
    let cached = readCachedUploads();
    if (cached.length === 0) return summary;

    // 1. Park what has failed too many times.
    const exhausted = cached.filter((u) => (u.attempts || 0) >= MAX_REPLAY_ATTEMPTS);
    parkUploads(exhausted, `${MAX_REPLAY_ATTEMPTS} failed replay attempts`);
    summary.parked = exhausted.length;
    cached = cached.filter((u) => !exhausted.includes(u));

    // 2. Coalesce: for each target keep only the newest item; drop the rest.
    const newestByKey = new Map();
    for (const u of cached) {
      const key = coalesceKey(u);
      const current = newestByKey.get(key);
      if (!current || (u.timestamp || 0) > (current.timestamp || 0)) newestByKey.set(key, u);
    }
    const keep = new Set([...newestByKey.values()].map((u) => u.id));
    const superseded = cached.filter((u) => !keep.has(u.id));
    if (superseded.length > 0) {
      writeUploads(FAILED_UPLOADS_KEY, readCachedUploads().filter((u) => keep.has(u.id)));
      summary.superseded = superseded.length;
      console.log(`Dropped ${superseded.length} superseded cached upload(s)`);
    }

    // 3. Replay sequentially, oldest first.
    const queue = [...newestByKey.values()].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    console.log(`Replaying ${queue.length} cached upload(s)`);
    for (const upload of queue) {
      // SECURITY CHECK: only retry records owned by the authenticated user.
      // Participant enrolment IDs may be `${b2cId}_${studyId}`. Leave others
      // in the cache for the original user to retry when they log back in.
      if (!isUploadOwnedByUser(upload, currentUserId)) {
        console.warn(`Skipping cross-user retry for cached upload ${upload.id}`);
        summary.skipped++;
        continue;
      }
      const method = (upload.method || "").toUpperCase();
      try {
        if (method === "POST") {
          await postData({
            endpoint: upload.endpoint,
            data: upload.data,
            accessToken,
            participantId: upload.participantId,
            fromCache: true,
            uploadId: upload.id,
          });
        } else if (method === "PATCH") {
          await patchData({
            endpoint: upload.endpoint,
            data: upload.data,
            accessToken,
            fromCache: true,
            uploadId: upload.id,
          });
        } else {
          summary.skipped++;
          continue;
        }
        summary.sent++;
      } catch (error) {
        summary.failed++;
        // A terminal 4xx has already removed the item (see postData/patchData);
        // only count attempts against items still in the cache.
        recordAttempt(upload.id);
        console.error(`Failed to retry upload ${upload.id}:`, error);
        if (error && error.status === 401) {
          console.warn("Token rejected during replay; stopping until next login");
          break;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, REPLAY_GAP_MS));
    }
    return summary;
  } finally {
    isProcessingUploads = false;
  }
}

async function postData({
  endpoint = "",
  data = {},
  timeout = 30000,
  accessToken = "",
  participantId = null,
  retries = 1,
  fromCache = false,
  uploadId = null,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(
        endpoint.startsWith("https://") ? endpoint : apiURLroot + endpoint,
        {
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: headers,
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify(data),
          signal: controller.signal,
        }
      );
      clearTimeout(id);
    } catch (e) {
      console.log("Request error:", e);
      // Don't cache here - we'll do it in the else block if needed
    }
    retries--;
  }

  if (response && response.ok) {
    // If this was a cached upload that succeeded, remove it from cache
    if (fromCache && uploadId) {
      removeFromCache(uploadId);
      if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(CACHED_UPLOAD_SUCCEEDED_EVENT, {
            detail: {
              endpoint,
              participantId,
              sessionCode: data?.sessionCode,
              taskParam: data?.taskParam,
            },
          })
        );
      }
    }
    return response.json();
  } else {
    const status = response?.status || 0;
    const retryable = isRetryableStatus(status);

    // Cache only temporary failures. A terminal response from a cached retry
    // must be removed so it does not retry forever.
    if (!fromCache && participantId && retryable) {
      cacheFailedUpload(endpoint, data, participantId, "post");
    } else if (!fromCache && !participantId && retryable) {
      console.warn(
        "Failed upload not cached: participantId was not provided to postData."
      );
    }

    let errorData = {};
    try {
      errorData = (await response?.json()) || {};
    } catch (e) {
      // Use the status-derived fallback below when the response has no JSON.
    }
    const requestError = {
      ...(typeof errorData === "object" ? errorData : {}),
      status,
      retryable,
      message:
        errorData?.message ||
        errorData?.error ||
        (response
          ? `Server error (${response.status}): ${response.statusText}`
          : "Request could not reach the server"),
    };

    if (fromCache && uploadId && !retryable) {
      removeFromCache(uploadId);
      if (typeof window !== "undefined" && typeof CustomEvent !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(CACHED_UPLOAD_FAILED_EVENT, {
            detail: {
              endpoint,
              participantId,
              sessionCode: data?.sessionCode,
              taskParam: data?.taskParam,
              status,
              message: requestError.message,
            },
          })
        );
      }
    }

    return Promise.reject(requestError);
  }
}

async function patchData({
  endpoint = "",
  data = {},
  timeout = 30000,
  accessToken = "",
  retries = 1,
  fromCache = false,
  uploadId = null,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(apiURLroot + endpoint, {
        method: "PATCH",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: headers,
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        signal: controller.signal,
      });
      clearTimeout(id);
    } catch (e) {
      console.log("Request error:", e);
      // Don't cache here - we'll do it in the else block if needed
    }
    retries--;
  }
  if (response && response.ok) {
    // If this was a cached upload that succeeded, remove it from cache
    if (fromCache && uploadId) {
      removeFromCache(uploadId);
    }
    return response.json();
  } else {
    // If this was a fresh upload (not a retry attempt), cache it for later
    if (!fromCache && data.id) {
      cacheFailedUpload(endpoint, data, data.id, "patch");
    }

    try {
      const errorData = await response.json();
      return Promise.reject(errorData);
    } catch (e) {
      return Promise.reject({
        status: response?.status || 0,
        message: response
          ? `Server error (${response.status}): ${response.statusText}`
          : "Request was cancelled",
      });
    }
  }
}

async function getData({
  endpoint = "",
  parameters = {},
  timeout = 30000,
  accessToken = "",
  retries = 1,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    let queryString = "";
    try {
      queryString = Object.keys(parameters).reduce(
        (qs, key) => qs + (qs === "" ? "?" : "&") + key + "=" + parameters[key],
        ""
      );
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(apiURLroot + endpoint + queryString, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: headers,
        redirect: "follow",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
      });
      clearTimeout(id);
    } catch (e) {
      console.log(e);
    }
    retries--;
  }
  if (response && response.ok) {
    return response.json();
  } else {
    try {
      const errorData = await response.json();
      return Promise.reject(errorData);
    } catch (e) {
      return Promise.reject({
        status: response.status,
        message: `Server error (${response.status}): ${response.statusText}`,
      });
    }
  }
}

// DELETE data function
async function deleteData({ endpoint, accessToken }) {
  const url = `${apiURLroot}${endpoint}`;
  const headers = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetch(url, {
    method: "DELETE",
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    return Promise.reject({
      status: response.status,
      message: errorData.error || `Server error (${response.status})`,
    });
  }

  // DELETE requests might not have a body
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  return {};
}

// Export the functions
export {
  postData,
  patchData,
  getData,
  deleteData,
  processFailedUploads,
  FAILED_UPLOADS_KEY,
  PARKED_UPLOADS_KEY,
  CACHED_UPLOAD_SUCCEEDED_EVENT,
  CACHED_UPLOAD_FAILED_EVENT,
  isRetryableStatus,
  isUploadOwnedByUser,
}; // Export key for use in notification component
